// src/interfaces/manager/ManagerCalculatorInterface.js
import React, { useState, useEffect } from 'react';
import ManagerCalculatorNavigation from './ManagerCalculatorNavigation';
import PriceCalculator from '../user/PriceCalculator';
import SmallFormatCalculator from '../user/SmallFormatCalculator';
import QuotesList from '../user/QuotesList';
import DraftsList from '../user/DraftsList';
import AddressBookPage from '../user/AddressBookPage';
import { useQuotes } from '../../context/QuoteContext';
import { useAuth } from '../../context/AuthContext';
import usePersistedState from '../../hooks/usePersistedState';

const ManagerCalculatorInterface = () => {
  const { currentUser } = useAuth();

  // State for calculator page navigation
  const [calculatorPage, setCalculatorPage] = usePersistedState(
    'managerCalc_activePage',
    'calculator',
    (value) => ['calculator', 'drafts', 'quotes', 'address-book'].includes(value)
  );

  // State for job type (large-format / small-format)
  const [jobType, setJobType] = usePersistedState(
    'managerCalc_jobType',
    'small-format',
    (value) => ['large-format', 'small-format'].includes(value)
  );

  // State for pricing tier (managers can override their profile tier)
  const [pricingTier, setPricingTier] = usePersistedState(
    'managerCalc_pricingTier',
    currentUser?.pricing_tier || 'retail',
    (value) => ['retail', 'partner', 'trade'].includes(value)
  );

  // State for selected customer (provides pricing tier context)
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const { targetCalculator, clearLoadedQuote, fetchQuotesWithContext, fetchDraftsWithContext } = useQuotes();

  // Handle auto-navigation when a quote is loaded for "Edit Similar"
  useEffect(() => {
    if (targetCalculator) {
      // Ensure we're on the calculator page
      if (calculatorPage !== 'calculator') {
        setCalculatorPage('calculator');
      }
      // Set the jobType to match the targeted calculator
      if (jobType !== targetCalculator) {
        setJobType(targetCalculator);
      }
    }
  }, [targetCalculator, calculatorPage, jobType, setCalculatorPage, setJobType]);

  // Fetch quotes and drafts when navigating to those pages
  useEffect(() => {
    if (calculatorPage === 'quotes' || calculatorPage === 'drafts') {
      // Fetch with management context to see all manager-created items
      fetchQuotesWithContext('management');
      fetchDraftsWithContext('management');
    }
  }, [calculatorPage, fetchQuotesWithContext, fetchDraftsWithContext]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-sky-50 to-slate-200">
      <ManagerCalculatorNavigation
        calculatorPage={calculatorPage}
        setCalculatorPage={setCalculatorPage}
        jobType={jobType}
        setJobType={setJobType}
        pricingTier={pricingTier}
        setPricingTier={setPricingTier}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
      />

      <div className={`container mx-auto px-4 ${calculatorPage === 'calculator' ? 'pb-6 pt-4' : 'py-6'}`}>
        <main>
          {calculatorPage === 'calculator' && jobType === 'large-format' && (
            <PriceCalculator
              pricingTier={pricingTier}
              selectedCustomer={selectedCustomer}
              onResetCustomer={() => setSelectedCustomer(null)}
            />
          )}
          {calculatorPage === 'calculator' && jobType === 'small-format' && (
            <SmallFormatCalculator
              pricingTier={pricingTier}
              selectedCustomer={selectedCustomer}
              onResetCustomer={() => setSelectedCustomer(null)}
            />
          )}
          {calculatorPage === 'drafts' && <DraftsList setCalculatorPage={setCalculatorPage} />}
          {calculatorPage === 'quotes' && <QuotesList setCalculatorPage={setCalculatorPage} />}
          {calculatorPage === 'address-book' && <AddressBookPage />}
        </main>
      </div>
    </div>
  );
};

export default ManagerCalculatorInterface;
